import {
    MoreVert,
    FormatListBulleted,
    ChevronRight,
    ArrowUpward,
    DragIndicator,
    Replay,
    Save,
    ArrowDownward,
    Close
} from '@mui/icons-material';
import {
    Badge,
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    useTheme,
    CircularProgress,
    Alert,
    Autocomplete,
    TextField,
    Box
} from '@mui/material';
import { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { FieldsOrder, UserPreferencesComponentKey } from '../../utils';
import { StyledMenu } from 'ui-component/RecordView/components/StyledMenu';
import { PanelFormsContext } from 'contexts/panelFormsContext';
import { IUserDataToken } from 'utils/types';
import { useDispatch } from 'store';
import { activeAction, activeForm } from 'store/slices/menu';
import { useConfirmationModalContext } from 'hooks/useConfirmationModal';
import { ReactComponent as SaveIcon } from 'assets/images/icons/save.svg';
import { ReactComponent as SaveNewIcon } from 'assets/images/icons/savenew.svg';
import { MetafieldPreferenceDialog } from './MetafieldPreferenceDialog';
import { QUERY_ME } from 'graphql/mutations/auth';
import { useQuery } from '@apollo/client';

const HeaderMenuItem = ({
    label,
    icon,
    iconRight,
    onClick,
    disabled
}: {
    label: string;
    icon: React.ReactNode;
    iconRight: React.ReactNode;
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
}) => (
    <MenuItem sx={{ alignItems: 'center', justifyContent: 'space-between' }} onClick={onClick} disabled={disabled}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
        {iconRight && <ListItemIcon sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>{iconRight}</ListItemIcon>}
    </MenuItem>
);

const OrderByMenu = ({
    anchorEl,
    setAnchorEl,
    onChangeFieldsOrder
}: {
    anchorEl: HTMLElement | null;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
    onChangeFieldsOrder: (newOrder: FieldsOrder) => void;
}) => {
    const orderByMenuItems = [
        {
            label: 'Without Order',
            icon: <FormatListBulleted />,
            iconRight: null,
            onClick: () => onChangeFieldsOrder(FieldsOrder.Default)
        },
        {
            label: 'By Field Types',
            icon: <FormatListBulleted />,
            iconRight: null,
            onClick: () => onChangeFieldsOrder(FieldsOrder.ByFieldType)
        },
        {
            label: 'By Data Types',
            icon: <FormatListBulleted />,
            iconRight: null,
            onClick: () => onChangeFieldsOrder(FieldsOrder.ByDataType)
        }
    ];

    return (
        <StyledMenu
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            minWidth="15.625rem"
        >
            {orderByMenuItems.map((item) => (
                <HeaderMenuItem key={item.label} label={item.label} icon={item.icon} iconRight={item.iconRight} onClick={item.onClick} />
            ))}
        </StyledMenu>
    );
};

const ActiveFiltersBadge = ({ showBadge, children }: { showBadge: boolean; children?: React.ReactNode }) => (
    <Badge color="error" variant="dot" invisible={!showBadge}>
        {children || <></>}
    </Badge>
);

export const HeaderMenu = ({ recordTypeId, isObjectPanel }: { recordTypeId: number; isObjectPanel: boolean }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const modal = useConfirmationModalContext();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [showBadge, setShowBadge] = useState(false);
    const [orderByMenuAnchor, setOrderByMenuAnchor] = useState<HTMLElement | null>(null);
    const [selectedMetafieldsPreference, setSelectedMetafieldsPreference] = useState<any>();
    const [showMetafieldsPreferenceDialog, setShowMetafieldsPreferenceDialog] = useState(false);
    const [metafieldSaveMode, setMetafieldSaveMode] = useState<'saveAsExisting' | 'saveAsNew'>();
    const [metafieldName, setMetafieldName] = useState('');
    const tenantId = localStorage.getItem('tenant_id');
    const { data: curUserData } = useQuery(QUERY_ME);
    const userData: IUserDataToken = curUserData?.me ? JSON.parse(curUserData.me) : null;

    const {
        setSort,
        setEnableDragAndDrop,
        setFieldsOrder,
        setCustomFieldOrder,
        setPinnedFields,
        setCustomObjectFieldsOrder,
        enableDragAndDrop,
        pinnedFields,
        sort,
        fieldsOrder,
        customFieldOrder,
        customObjectFieldsOrder,
        userPreferences,
        getUserPreferences,
        createOrUpdateUserPreferences,
        updateLoading,
        loading
    } = useContext(PanelFormsContext);

    const validateHasChanges = useCallback(() => {
        if (Object.keys(userPreferences).length === 0) return false;
        const { preferenceData } = selectedMetafieldsPreference || {};

        const {
            sort: userPreferencesSort,
            fieldsOrder: userPreferencesFieldsOrder,
            customFieldOrder: userPreferencesCustomFieldOrder,
            customObjectFieldsOrder: userPreferencesCustomObjectFieldsOrder,
            pinnedFields: userPreferencesPinnedFields
        } = preferenceData;

        const validateValue = (value: any, userPreferencesValue: any) => value !== userPreferencesValue;

        const validateArray = (value: any, userPreferencesValue: any) =>
            JSON.stringify(value) !== JSON.stringify(userPreferencesValue || []);

        const hasSortChanges = validateValue(sort, userPreferencesSort);
        const hasFieldsOrderChanges = validateValue(fieldsOrder, userPreferencesFieldsOrder);
        const hasCustomFieldOrderChanges = validateArray(customFieldOrder, userPreferencesCustomFieldOrder);
        const hasCustomObjectFieldsOrderChanges = validateArray(customObjectFieldsOrder, userPreferencesCustomObjectFieldsOrder);
        const hasPinnedFieldsChanges = validateArray(pinnedFields, userPreferencesPinnedFields);

        return (
            hasSortChanges ||
            hasFieldsOrderChanges ||
            hasCustomFieldOrderChanges ||
            hasCustomObjectFieldsOrderChanges ||
            hasPinnedFieldsChanges
        );
    }, [userPreferences, pinnedFields, fieldsOrder, customFieldOrder, customObjectFieldsOrder, sort, selectedMetafieldsPreference]);

    const handleChangeFieldsOrder = (newOrder: FieldsOrder) => {
        setFieldsOrder(newOrder);
        setAnchorEl(null);
        setOrderByMenuAnchor(null);
    };

    const handleSavePropertiesPanel = async () => {
        await modal.showConfirmation({
            title: 'Are you sure you want to discard your changes?',
            content: (
                <Alert
                    variant="filled"
                    icon={false}
                    sx={{
                        bgcolor: '#FB9000',
                        color: 'white',
                        borderRadius: '8px !important',
                        height: '32px',
                        fontSize: '14px',
                        width: 'fit-content',
                        padding: '0 16px !important',
                        fontWeight: 500
                    }}
                >
                    This is an irreversible action and will permanently remove your work.
                </Alert>
            ),
            loadingText: 'Saving Changes',
            forwardButtonText: 'Save changes',
            actionButtonText: 'Discard Changes',
            onForward: async () => {
                modal.toogleLoading(true);
                await handleCreateOrUpdateUserPreferences();
                modal.toogleLoading(false);
            },
            onAction: () => {
                modal.toogleLoading(false);
            },
            showCheckbox: true,
            actionType: `save_properties_panel`,
            sectionTitle: 'record_viewer',
            recordType: '',
            hideCloseBtn: true
        });
    };

    const handleCreateOrUpdateUserPreferences = async (name?: string) => {
        const order = {
            fieldsOrder,
            customFieldOrder,
            sort,
            pinnedFields,
            customObjectFieldsOrder
        };

        const variables = {
            componentKey: UserPreferencesComponentKey.DetailsPanel,
            recordTypeId,
            preferenceData: order,
            name: name || 'Details Panel',
            isDefault: true,
            userId: userData.userId,
            tenantId: Number(tenantId)
        };
        const { data } = await createOrUpdateUserPreferences(variables);
        console.log('data', data);
        setAnchorEl(null);
        setShowMetafieldsPreferenceDialog(false);
        setSelectedMetafieldsPreference(data?.createOrUpdateUserPreferences);
    };

    const handleSaveMatafieldsPreference = (saveAsOpt: 'saveAsExisting' | 'saveAsNew') => {
        handleCreateOrUpdateUserPreferences(saveAsOpt === 'saveAsExisting' ? selectedMetafieldsPreference?.name : metafieldName);
    };

    const handleRestoreOrder = useCallback(() => {
        if (userPreferences) {
            const {
                sort: userPreferencesSort,
                fieldsOrder: userPreferencesFieldsOrder,
                customFieldOrder: userPreferencesCustomFieldOrder,
                customObjectFieldsOrder: userPreferencesCustomObjectFieldsOrder,
                pinnedFields: userPreferencesPinnedFields
            } = userPreferences.preferenceData;
            setSort(userPreferencesSort || 'none');
            setFieldsOrder(userPreferencesFieldsOrder || FieldsOrder.Default);
            setCustomFieldOrder(userPreferencesCustomFieldOrder || []);
            setPinnedFields(userPreferencesPinnedFields || []);
            setCustomObjectFieldsOrder(userPreferencesCustomObjectFieldsOrder || []);
        }
    }, [userPreferences, setSort, setFieldsOrder, setCustomFieldOrder, setPinnedFields, setCustomObjectFieldsOrder]);

    const handleEnableDragAndDrop = useCallback(
        (newEnableDragAndDrop: boolean) => {
            if (!newEnableDragAndDrop) {
                handleRestoreOrder();
            }

            setEnableDragAndDrop?.(newEnableDragAndDrop);
        },
        [setEnableDragAndDrop, handleRestoreOrder]
    );

    const customFieldOrderChanges = useMemo(
        () => JSON.stringify(customFieldOrder) !== JSON.stringify(userPreferences?.preferenceData?.customFieldOrder || []),
        [customFieldOrder, userPreferences]
    );

    const menuItems = useMemo(() => {
        const getMenuItems = () => {
            const baseItems = [
                {
                    label: 'Order by',
                    icon: <FormatListBulleted />,
                    iconRight: <ChevronRight />,
                    onClick: (e: React.MouseEvent<HTMLElement>) => setOrderByMenuAnchor(e.currentTarget),
                    disabled: false
                },
                {
                    label: enableDragAndDrop ? 'Cancel Order' : 'Edit Order',
                    icon: enableDragAndDrop ? <Close /> : <DragIndicator />,
                    iconRight: customFieldOrderChanges && <ActiveFiltersBadge showBadge />,
                    onClick: () => handleEnableDragAndDrop(!enableDragAndDrop),
                    disabled: isObjectPanel
                }
            ];

            if (sort === 'asc' || sort === 'desc') {
                const removeSortItem = {
                    label: sort === 'asc' ? 'Remove Sort ascending' : 'Remove Sort descending',
                    icon: <Close />,
                    iconRight: null,
                    onClick: () => setSort?.('none'),
                    disabled: false
                };

                const otherSortItem = {
                    label: sort === 'asc' ? 'Sort descending' : 'Sort ascending',
                    icon: sort === 'asc' ? <ArrowDownward /> : <ArrowUpward />,
                    iconRight: null,
                    onClick: () => setSort?.(sort === 'asc' ? 'desc' : 'asc'),
                    disabled: false
                };

                return [otherSortItem, removeSortItem, ...baseItems];
            }

            const sortItems = [
                {
                    label: 'Sort ascending',
                    icon: <ArrowUpward />,
                    iconRight: null,
                    onClick: () => setSort?.('asc'),
                    disabled: false
                },
                {
                    label: 'Sort descending',
                    icon: <ArrowDownward />,
                    iconRight: null,
                    onClick: () => setSort?.('desc'),
                    disabled: false
                }
            ];

            return [...sortItems, ...baseItems];
        };

        return getMenuItems();
    }, [enableDragAndDrop, customFieldOrderChanges, sort, setSort, handleEnableDragAndDrop, setOrderByMenuAnchor, isObjectPanel]);

    useEffect(() => {
        getUserPreferences(recordTypeId);
    }, [getUserPreferences, recordTypeId]);

    useEffect(() => {
        if (userPreferences && !loading) {
            setSelectedMetafieldsPreference(userPreferences?.find((preference: any) => preference.isDefault));
        }
    }, [userPreferences, loading]);

    useEffect(() => {
        if (selectedMetafieldsPreference) {
            setSort(selectedMetafieldsPreference.preferenceData.sort);
            setFieldsOrder(selectedMetafieldsPreference.preferenceData.fieldsOrder);
            setCustomFieldOrder(selectedMetafieldsPreference.preferenceData.customFieldOrder);
            setCustomObjectFieldsOrder(selectedMetafieldsPreference.preferenceData.customObjectFieldsOrder);
            setPinnedFields(selectedMetafieldsPreference.preferenceData.pinnedFields);
        }
    }, [selectedMetafieldsPreference, setSort, setFieldsOrder, setCustomFieldOrder, setCustomObjectFieldsOrder, setPinnedFields]);

    useEffect(() => {
        if (!selectedMetafieldsPreference) return;
        const hasChanges = validateHasChanges();
        if (hasChanges) {
            dispatch(activeAction('preferences'));
            dispatch(activeForm('preferences'));
        } else {
            dispatch(activeAction(null));
            dispatch(activeForm(null));
        }
        setShowBadge(hasChanges);
    }, [validateHasChanges, dispatch, selectedMetafieldsPreference]);

    return (
        <>
            <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    bgcolor: anchorEl ? theme.palette.secondary.light : theme.palette.primary[300],
                    '&:hover': { bgcolor: theme.palette.secondary.light, color: theme.palette.primary.dark },
                    height: '2rem',
                    width: '2rem'
                }}
            >
                <ActiveFiltersBadge showBadge={showBadge}>
                    <MoreVert sx={{ fontSize: '1.5rem' }} />
                </ActiveFiltersBadge>
            </IconButton>
            <StyledMenu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                minWidth="18.75rem"
            >
                <MenuItem
                    sx={{ display: 'flex', gap: '8px', alignItems: 'center', '&:hover': { backgroundColor: '#F5F6F7 !important' } }}
                    disableRipple
                >
                    <Autocomplete
                        options={userPreferences}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => +option.id === +value.id}
                        value={selectedMetafieldsPreference}
                        onChange={(event, newValue) => setSelectedMetafieldsPreference(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="filled"
                                size="small"
                                fullWidth
                                InputProps={{ ...params.InputProps, disableUnderline: true, hiddenLabel: true }}
                                sx={{ '& .MuiFilledInput-root': { height: '34px', borderRadius: '10px', py: 0, backgroundColor: '#fff' } }}
                            />
                        )}
                        sx={{ flexGrow: 1 }}
                    />
                    <Box>
                        <IconButton
                            disabled={selectedMetafieldsPreference?.name === 'Default Metafield Preference'}
                            onClick={() => {
                                setShowMetafieldsPreferenceDialog(true);
                                setMetafieldSaveMode('saveAsExisting');
                                setMetafieldName(selectedMetafieldsPreference?.name);
                            }}
                            sx={{ height: '38px', width: '34px', padding: 0, backgroundColor: '#fff', mr: '5px' }}
                        >
                            <SaveIcon />
                        </IconButton>
                        <IconButton
                            onClick={() => {
                                setShowMetafieldsPreferenceDialog(true);
                                setMetafieldSaveMode('saveAsNew');
                                setMetafieldName('');
                            }}
                            sx={{ height: '38px', width: '34px', padding: 0, backgroundColor: '#fff' }}
                        >
                            <SaveNewIcon />
                        </IconButton>
                    </Box>
                </MenuItem>
                {menuItems.map((item) => (
                    <HeaderMenuItem
                        key={item.label}
                        label={item.label}
                        icon={item.icon}
                        iconRight={item.iconRight}
                        onClick={(e) => item.onClick(e)}
                        disabled={item?.disabled}
                    />
                ))}
                <MenuItem
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '0.625rem',
                        '&:hover, &:active': { bgcolor: 'transparent !important' }
                    }}
                    disableRipple
                >
                    <Button
                        startIcon={<Replay />}
                        variant="contained"
                        sx={{
                            borderRadius: '2rem',
                            bgcolor: theme.palette.background.paper,
                            color: theme.palette.primary['400'],
                            padding: '0.125rem 0.725rem',
                            '&:hover': { bgcolor: theme.palette.background.paper }
                        }}
                        onClick={handleRestoreOrder}
                    >
                        Restore
                    </Button>
                    <Button
                        startIcon={updateLoading ? <CircularProgress size={16} /> : <Save />}
                        variant="contained"
                        sx={{
                            borderRadius: '2rem',
                            padding: '0.125rem 0.725rem'
                        }}
                        disabled={!showBadge || loading || updateLoading}
                        onClick={() => handleSavePropertiesPanel()}
                    >
                        Save Order
                    </Button>
                </MenuItem>
            </StyledMenu>
            <OrderByMenu anchorEl={orderByMenuAnchor} setAnchorEl={setOrderByMenuAnchor} onChangeFieldsOrder={handleChangeFieldsOrder} />
            <MetafieldPreferenceDialog
                showMetafieldsPreferenceDialog={showMetafieldsPreferenceDialog}
                setShowMetafieldsPreferenceDialog={setShowMetafieldsPreferenceDialog}
                metafieldSaveMode={metafieldSaveMode}
                metafieldName={metafieldName}
                setMetafieldName={setMetafieldName}
                handleSaveMatafieldsPreference={handleSaveMatafieldsPreference}
                loading={updateLoading}
            />
        </>
    );
};
